//! Renders frozen (pinned) rows and columns.
//!
//! After the normal render of the stage, the current sheet's cells container
//! (`content.cellsSheet`) plus a pinned set of grid lines are re-rendered up to
//! three additional times — one per pane (corner, top frozen rows, left frozen
//! columns) — each with a pinned transform and a single full-pass `gl.scissor`
//! clipping it to the pane rect.
//!
//! This reuses the already-built GPU meshes (no regeneration, no per-object
//! masks). A full-pass scissor is one GL call per pass and does not break
//! batching. Each pane is cleared to the grid background first so the scrolled
//! body that slides under the frozen strips is covered.
//!
//! A divider line is drawn at the freeze boundary only when the body has
//! scrolled past it (i.e., when the frozen content is pinned somewhere other
//! than where the body would naturally draw it).
//!
//! Note: the frozen-band hashes must stay loaded in the render worker and
//! visible on the main thread (see CellsLabels) so they exist to draw here.

import { sheets } from '@/app/grid/controller/Sheets';
import type { Sheet } from '@/app/grid/sheet/Sheet';
import type { CellsSheet } from '@/app/gridGL/cells/CellsSheet';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { pixiApp } from '@/app/gridGL/pixiApp/PixiApp';
import { calculateAlphaForGridLines } from '@/app/gridGL/UI/gridUtils';
import { getCSSVariableTint } from '@/app/helpers/convertColor';
import { isMobileMode } from '@/app/helpers/isMobile';
import { colors } from '@/app/theme/colors';
import { SNACKBAR_DURATION } from '@/shared/components/GlobalSnackbarProvider';
import { Graphics, Point, type Renderer } from 'pixi.js';

interface PaneRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const hexToRgbNormalized = (hex: number): [number, number, number] => {
  return [((hex >> 16) & 0xff) / 255, ((hex >> 8) & 0xff) / 255, (hex & 0xff) / 255];
};

export class FrozenPanes {
  // grid lines for the frozen panes (drawn in world coords, rendered pinned)
  private lines = new Graphics();

  // temporary pane-rect mask for the native charts render (see renderPane)
  private paneMask = new Graphics();

  // divider drawn at the freeze boundary (in screen coords)
  private divider = new Graphics();

  // live preview line while dragging the freeze divider (in screen coords)
  private preview = new Graphics();

  // Tracks whether the frozen panes currently consume the entire viewport so we
  // only warn the user once per time the condition is entered (render runs every
  // frame), and warn again if they leave and re-enter the state.
  private warnedFillsViewport = false;

  // Tracks whether the fills-viewport warning snackbar is still on screen so we
  // don't stack a second one when the viewport briefly leaves and re-enters the
  // filled state (e.g. while zooming in and out). Cleared after the snackbar's
  // display duration elapses.
  private fillsViewportWarningTimeout: ReturnType<typeof setTimeout> | undefined;

  // Maps a screen point to the world coordinate of the cell shown there when
  // the point falls inside a frozen pane (corner / top rows / left columns).
  // Returns `fallback` (the normal viewport world point) when the point is over
  // the scrollable body or headings.
  getFrozenWorld(global: Point, fallback: Point): Point {
    try {
      const sheet = sheets.sheet;
      if (!sheet || (sheet.frozenRows === 0 && sheet.frozenColumns === 0)) return fallback;

      const viewport = pixiApp.viewport;
      const scale = viewport.scale.x;
      const headingWidth = content.headings.headingSize.width;
      const headingHeight = content.headings.headingSize.height;

      const sx = global.x;
      const sy = global.y;

      // headings are handled by their own pointer logic
      if (sx < headingWidth || sy < headingHeight) return fallback;

      const boundary = sheet.getFrozenBoundary();
      // Pinned origin matches the render pass: clamped to the heading edge, but
      // following the viewport origin when it drifts past it (e.g. zooming out).
      const originX = Math.max(headingWidth, viewport.x);
      const originY = Math.max(headingHeight, viewport.y);
      const freezeScreenX = originX + boundary.x * scale;
      const freezeScreenY = originY + boundary.y * scale;

      const inFrozenColumns = sheet.frozenColumns > 0 && sx < freezeScreenX;
      const inFrozenRows = sheet.frozenRows > 0 && sy < freezeScreenY;

      if (!inFrozenColumns && !inFrozenRows) return fallback;

      // x is pinned to the frozen origin in the frozen-columns region, otherwise
      // it scrolls with the viewport; y is pinned in the frozen-rows region.
      const worldX = (sx - (inFrozenColumns ? originX : viewport.x)) / scale;
      const worldY = (sy - (inFrozenRows ? originY : viewport.y)) / scale;
      return new Point(worldX, worldY);
    } catch (e) {
      console.error('[FrozenPanes] getFrozenWorld error', e);
      return fallback;
    }
  }

  // Adjusts a world point so that, when positioned inside the viewport-
  // transformed HTML overlay layer (HTMLGridContainer), it lands at the cell's
  // displayed (pinned) location when the cell is in a frozen pane. For cells
  // outside the frozen region (or when nothing is frozen) the point is
  // returned unchanged. Use this for UI overlays (context menus, inline
  // editor, etc.) whose target may be a frozen cell.
  getDisplayWorld(world: Point): Point {
    const sheet = sheets.sheet;
    if (!sheet || (sheet.frozenRows === 0 && sheet.frozenColumns === 0)) return world;

    const viewport = pixiApp.viewport;
    const scale = viewport.scale.x;
    const bounds = viewport.getVisibleBounds();
    const headingWidthWorld = content.headings.headingSize.width / scale;
    const headingHeightWorld = content.headings.headingSize.height / scale;
    const boundary = sheet.getFrozenBoundary();

    let x = world.x;
    let y = world.y;
    // Shift by the scrolled distance on the frozen axis so the overlay tracks
    // the pinned cell instead of the scrolled-away world position. The shift is
    // clamped to zero so that when the origin drifts past the heading edge (e.g.
    // zooming out) the overlay follows the body's origin — where the frozen
    // content is now re-pinned — rather than over-shifting under the headings.
    if (sheet.frozenColumns > 0 && world.x < boundary.x) {
      x = world.x + Math.max(0, bounds.left + headingWidthWorld);
    }
    if (sheet.frozenRows > 0 && world.y < boundary.y) {
      y = world.y + Math.max(0, bounds.top + headingHeightWorld);
    }
    return new Point(x, y);
  }

  // Renders the frozen panes. Must be called after the main stage render so it
  // composites on top of the scrolled body.
  render(renderer: Renderer): void {
    const sheet = sheets.sheet;
    if (!sheet) return;

    if (sheet.frozenRows === 0 && sheet.frozenColumns === 0) {
      this.warnedFillsViewport = false;
      // Nothing is frozen, but the user may be dragging a corner freeze handle.
      // Draw the live preview line so the prospective boundary is visible.
      const headingWidth = content.headings.headingSize.width;
      const headingHeight = content.headings.headingSize.height;
      this.renderPreview(renderer, headingWidth, headingHeight, renderer.screen.width, renderer.screen.height);
      if (content.headings.freezePreview) {
        renderer.objectRenderer.lastObjectRendered = pixiApp.stage;
      }
      return;
    }

    const cellsSheet = content.cellsSheets.current;
    if (!cellsSheet) return;

    const viewport = pixiApp.viewport;
    const scale = viewport.scale.x;
    const vx = viewport.x;
    const vy = viewport.y;

    const headingWidth = content.headings.headingSize.width;
    const headingHeight = content.headings.headingSize.height;

    const boundary = sheet.getFrozenBoundary();

    // Pin the frozen content to wherever world (0,0) is actually drawn, clamped
    // to the heading edge. When the body scrolls past the freeze line the origin
    // would slide under the headings (vx/vy < heading), so we keep the strip at
    // the heading edge and let the body scroll under it. When the origin drifts
    // the other way (vx/vy > heading, e.g. while zooming out) we follow it so the
    // frozen strip stays glued to the body's origin instead of the heading edge.
    const originX = Math.max(headingWidth, vx);
    const originY = Math.max(headingHeight, vy);

    const freezeScreenX = originX + boundary.x * scale;
    const freezeScreenY = originY + boundary.y * scale;

    const screenW = renderer.screen.width;
    const screenH = renderer.screen.height;

    this.checkFillsViewport(sheet, freezeScreenX, freezeScreenY, screenW, screenH);

    // save the current transforms so we can restore them after the extra passes
    const savedX = cellsSheet.position.x;
    const savedY = cellsSheet.position.y;
    const savedScaleX = cellsSheet.scale.x;
    const savedScaleY = cellsSheet.scale.y;
    const cursor = content.uiCursor;
    const savedCursorX = cursor.position.x;
    const savedCursorY = cursor.position.y;
    const savedCursorScaleX = cursor.scale.x;
    const savedCursorScaleY = cursor.scale.y;
    const chartPlaceholders = content.chartPlaceholders;
    const savedChartX = chartPlaceholders.position.x;
    const savedChartY = chartPlaceholders.position.y;
    const savedChartScaleX = chartPlaceholders.scale.x;
    const savedChartScaleY = chartPlaceholders.scale.y;
    const chartsLayer = content.chartsLayer;
    const savedChartsLayerX = chartsLayer.position.x;
    const savedChartsLayerY = chartsLayer.position.y;
    const savedChartsLayerScaleX = chartsLayer.scale.x;
    const savedChartsLayerScaleY = chartsLayer.scale.y;
    const boxCells = content.boxCells;
    const savedBoxX = boxCells.position.x;
    const savedBoxY = boxCells.position.y;
    const savedBoxScaleX = boxCells.scale.x;
    const savedBoxScaleY = boxCells.scale.y;
    const cellMoving = content.cellMoving;
    const savedMovingX = cellMoving.position.x;
    const savedMovingY = cellMoving.position.y;
    const savedMovingScaleX = cellMoving.scale.x;
    const savedMovingScaleY = cellMoving.scale.y;
    const cellHighlights = content.cellHighlights;
    const savedHighlightsX = cellHighlights.position.x;
    const savedHighlightsY = cellHighlights.position.y;
    const savedHighlightsScaleX = cellHighlights.scale.x;
    const savedHighlightsScaleY = cellHighlights.scale.y;
    const mobileChartPlaceholders = content.mobileChartPlaceholders;
    const savedMobileChartX = mobileChartPlaceholders.position.x;
    const savedMobileChartY = mobileChartPlaceholders.position.y;
    const savedMobileChartScaleX = mobileChartPlaceholders.scale.x;
    const savedMobileChartScaleY = mobileChartPlaceholders.scale.y;

    const hasRows = sheet.frozenRows > 0;
    const hasCols = sheet.frozenColumns > 0;

    const gl = renderer.gl;

    // A try/finally guarantees the GL scissor is disabled and the shared
    // transforms are restored even if a render pass throws. Leaving the
    // scissor enabled would clip the next frame's full-canvas render (a
    // frozen-looking canvas), and leaving the transforms mutated would
    // mis-place the body on the next frame.
    try {
      gl.enable(gl.SCISSOR_TEST);

      const visibleBounds = viewport.getVisibleBounds();
      const offsets = sheet.offsets;
      const visibleStartCol = offsets.getColumnFromScreen(visibleBounds.left);
      const visibleEndCol = offsets.getColumnFromScreen(visibleBounds.right);
      const visibleStartRow = offsets.getRowFromScreen(visibleBounds.top);
      const visibleEndRow = offsets.getRowFromScreen(visibleBounds.bottom);
      const gridAlpha = gridSettings.showGridLines() ? calculateAlphaForGridLines(scale) : 0;

      // corner (frozen rows ∩ frozen columns) — pinned in both axes
      if (hasRows && hasCols) {
        this.drawGridLines(sheet, 1, sheet.frozenColumns, 1, sheet.frozenRows, gridAlpha);
        this.renderPane(
          renderer,
          cellsSheet,
          scale,
          {
            x: headingWidth,
            y: headingHeight,
            width: freezeScreenX - headingWidth,
            height: freezeScreenY - headingHeight,
          },
          originX,
          originY
        );
      }

      // top frozen rows — scrolls horizontally, pinned vertically
      if (hasRows) {
        this.drawGridLines(sheet, visibleStartCol, visibleEndCol, 1, sheet.frozenRows, gridAlpha);
        this.renderPane(
          renderer,
          cellsSheet,
          scale,
          { x: freezeScreenX, y: headingHeight, width: screenW - freezeScreenX, height: freezeScreenY - headingHeight },
          vx,
          originY
        );
      }

      // left frozen columns — scrolls vertically, pinned horizontally
      if (hasCols) {
        this.drawGridLines(sheet, 1, sheet.frozenColumns, visibleStartRow, visibleEndRow, gridAlpha);
        this.renderPane(
          renderer,
          cellsSheet,
          scale,
          { x: headingWidth, y: freezeScreenY, width: freezeScreenX - headingWidth, height: screenH - freezeScreenY },
          originX,
          vy
        );
      }
    } finally {
      gl.disable(gl.SCISSOR_TEST);

      // Restore the local transforms AND recompute the world transforms.
      // Rendering these containers as roots mutated their cached
      // worldTransform; pixi's event hit-testing reads that cached transform,
      // so without recomputing it here a click would be hit-tested against the
      // pinned (wrong) transform and never reach the pointer handlers.
      cellsSheet.position.set(savedX, savedY);
      cellsSheet.scale.set(savedScaleX, savedScaleY);
      cursor.position.set(savedCursorX, savedCursorY);
      cursor.scale.set(savedCursorScaleX, savedCursorScaleY);
      chartPlaceholders.position.set(savedChartX, savedChartY);
      chartPlaceholders.scale.set(savedChartScaleX, savedChartScaleY);
      chartsLayer.position.set(savedChartsLayerX, savedChartsLayerY);
      chartsLayer.scale.set(savedChartsLayerScaleX, savedChartsLayerScaleY);
      mobileChartPlaceholders.position.set(savedMobileChartX, savedMobileChartY);
      mobileChartPlaceholders.scale.set(savedMobileChartScaleX, savedMobileChartScaleY);
      boxCells.position.set(savedBoxX, savedBoxY);
      boxCells.scale.set(savedBoxScaleX, savedBoxScaleY);
      cellMoving.position.set(savedMovingX, savedMovingY);
      cellMoving.scale.set(savedMovingScaleX, savedMovingScaleY);
      cellHighlights.position.set(savedHighlightsX, savedHighlightsY);
      cellHighlights.scale.set(savedHighlightsScaleX, savedHighlightsScaleY);
      cellsSheet.updateTransform();
      cursor.updateTransform();
      chartPlaceholders.updateTransform();
      chartsLayer.updateTransform();
      mobileChartPlaceholders.updateTransform();
      boxCells.updateTransform();
      cellMoving.updateTransform();
      cellHighlights.updateTransform();
    }

    // freeze bars are always shown at the boundary when an axis is frozen
    this.renderDivider(renderer, {
      freezeScreenX,
      freezeScreenY,
      headingWidth,
      headingHeight,
      screenW,
      screenH,
      showVertical: hasCols,
      showHorizontal: hasRows,
    });

    // live preview while dragging the freeze divider (drawn on top of the
    // panes so it stays visible when dragged into the frozen zone)
    this.renderPreview(renderer, headingWidth, headingHeight, screenW, screenH);

    // CRITICAL: pixi's EventSystem hit-tests every pointer event against
    // `renderer.lastObjectRendered`. Each `renderer.render(...)` call above set
    // it to the frozen-pane object it rendered, which would leave pointer
    // events with the wrong hit-test root and stop them from reaching the grid
    // (clicks do nothing on sheets with frozen panes). Restore it to the stage.
    //
    // `renderer.lastObjectRendered` (pixi v7) is a read-only getter that returns
    // `renderer.objectRenderer.lastObjectRendered`, so we write that backing
    // field directly — there is no public setter.
    renderer.objectRenderer.lastObjectRendered = pixiApp.stage;
  }

  // Warns the user when the frozen rows/columns (plus headings) consume the
  // entire viewport, leaving no scrollable body on a frozen axis. This can
  // happen after zooming in or resizing the window. The snackbar fires once per
  // time the condition is entered.
  private checkFillsViewport(
    sheet: Sheet,
    freezeScreenX: number,
    freezeScreenY: number,
    screenW: number,
    screenH: number
  ): void {
    // ignore until we have a real viewport (avoids false positives during init)
    if (screenW <= 0 || screenH <= 0) return;

    const columnsFillWidth = sheet.frozenColumns > 0 && freezeScreenX >= screenW;
    const rowsFillHeight = sheet.frozenRows > 0 && freezeScreenY >= screenH;
    const fillsViewport = columnsFillWidth || rowsFillHeight;

    if (fillsViewport && !this.warnedFillsViewport && this.fillsViewportWarningTimeout === undefined) {
      const subject =
        columnsFillWidth && rowsFillHeight
          ? 'Frozen rows and columns'
          : columnsFillWidth
            ? 'Frozen columns'
            : 'Frozen rows';
      gridSettings.snackbar(`${subject} fill the entire viewport. Zoom out or unfreeze cells to scroll your content.`, {
        severity: 'warning',
        button: {
          title: 'Unfreeze all',
          callback: () => sheet.setFrozen(0, 0, false),
        },
      });
      this.fillsViewportWarningTimeout = setTimeout(() => {
        this.fillsViewportWarningTimeout = undefined;
      }, SNACKBAR_DURATION);
    }
    this.warnedFillsViewport = fillsViewport;
  }

  // Draws grid lines for a pane's cell range into `this.lines` (world coords).
  private drawGridLines(
    sheet: Sheet,
    colStart: number,
    colEnd: number,
    rowStart: number,
    rowEnd: number,
    gridAlpha: number
  ): void {
    this.lines.clear();
    if (gridAlpha === 0) return;

    const offsets = sheet.offsets;
    const left = offsets.getColumnPlacement(colStart).position;
    const right = offsets.getColumnPlacement(colEnd + 1).position;
    const top = offsets.getRowPlacement(rowStart).position;
    const bottom = offsets.getRowPlacement(rowEnd + 1).position;

    this.lines.lineStyle({ width: 1, color: colors.gridLines, alpha: 0.2 * gridAlpha, alignment: 0.5, native: true });

    for (let col = colStart; col <= colEnd + 1; col++) {
      const x = offsets.getColumnPlacement(col).position;
      this.lines.moveTo(x, top);
      this.lines.lineTo(x, bottom);
    }
    for (let row = rowStart; row <= rowEnd + 1; row++) {
      const y = offsets.getRowPlacement(row).position;
      this.lines.moveTo(left, y);
      this.lines.lineTo(right, y);
    }
  }

  // Sets the GL scissor to a canvas-screen rect (origin bottom-left, in
  // framebuffer pixels). Caller must enable/disable SCISSOR_TEST.
  private setScissor(renderer: Renderer, rect: PaneRect): void {
    const gl = renderer.gl;
    const resolution = renderer.resolution;
    const framebufferHeight = renderer.view.height;
    const glX = Math.round(rect.x * resolution);
    const glY = Math.round(framebufferHeight - (rect.y + rect.height) * resolution);
    const glWidth = Math.round(rect.width * resolution);
    const glHeight = Math.round(rect.height * resolution);
    gl.scissor(glX, glY, glWidth, glHeight);
  }

  private renderPane(
    renderer: Renderer,
    cellsSheet: CellsSheet,
    scale: number,
    rect: PaneRect,
    translateX: number,
    translateY: number
  ): void {
    if (rect.width <= 0 || rect.height <= 0) return;

    const gl = renderer.gl;

    this.setScissor(renderer, rect);

    // opaque background so the scrolled body underneath is covered
    const [r, g, b] = hexToRgbNormalized(colors.gridBackground);
    gl.clearColor(r, g, b, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);

    // pin the cells container and re-render it (clipped to the scissor rect)
    cellsSheet.position.set(translateX, translateY);
    cellsSheet.scale.set(scale, scale);
    renderer.render(cellsSheet, { clear: false });

    // pinned grid lines on top of the cells
    this.lines.position.set(translateX, translateY);
    this.lines.scale.set(scale, scale);
    renderer.render(this.lines, { clear: false });

    // pinned chart placeholders on top of the grid lines (so they cover them,
    // matching the body), filling the slices the live iframe doesn't cover
    const chartPlaceholders = content.chartPlaceholders;
    chartPlaceholders.position.set(translateX, translateY);
    chartPlaceholders.scale.set(scale, scale);
    renderer.render(chartPlaceholders, { clear: false });

    // mobile chart images (unselected) use the same torn-slice pass
    if (isMobileMode()) {
      const mobileChartPlaceholders = content.mobileChartPlaceholders;
      mobileChartPlaceholders.position.set(translateX, translateY);
      mobileChartPlaceholders.scale.set(scale, scale);
      renderer.render(mobileChartPlaceholders, { clear: false });
    }

    // pinned autocomplete/table-resize box preview (drawn in world coords),
    // kept below the cursor to match the body's z-order
    const boxCells = content.boxCells;
    boxCells.position.set(translateX, translateY);
    boxCells.scale.set(scale, scale);
    renderer.render(boxCells, { clear: false });

    // pinned cursor/selection on top so it shows in the frozen cells
    const cursor = content.uiCursor;
    cursor.position.set(translateX, translateY);
    cursor.scale.set(scale, scale);
    renderer.render(cursor, { clear: false });

    // Pinned native charts (drawn in world coords, matching the body z-order:
    // above the cursor/selection so charts cover the cell selection beneath
    // them, below the highlights/move chrome).
    //
    // Charts contain pixi rect masks (plot-area clips) which pixi implements
    // as scissor masks: they overwrite the manual gl.scissor during the
    // render and disable SCISSOR_TEST when pixi's mask stack empties. Wrap
    // the layer in a pane-rect mask so pixi's scissor stack clips charts (and
    // their nested plot-area clips) to the pane, then restore the manual
    // scissor for the remaining pane draws — otherwise the next pane's
    // full-canvas gl.clear wipes the already-rendered body.
    const chartsLayer = content.chartsLayer;
    chartsLayer.position.set(translateX, translateY);
    chartsLayer.scale.set(scale, scale);
    this.paneMask.clear();
    this.paneMask.beginFill(0xffffff);
    // the mask inherits the pinned transform, so convert the screen-space
    // pane rect back into world coordinates
    this.paneMask.drawRect(
      (rect.x - translateX) / scale,
      (rect.y - translateY) / scale,
      rect.width / scale,
      rect.height / scale
    );
    this.paneMask.endFill();
    chartsLayer.addChild(this.paneMask);
    chartsLayer.mask = this.paneMask;
    try {
      renderer.render(chartsLayer, { clear: false });
    } finally {
      chartsLayer.mask = null;
      chartsLayer.removeChild(this.paneMask);
      gl.enable(gl.SCISSOR_TEST);
      this.setScissor(renderer, rect);
    }

    // pinned source-range highlights (formula/code references, chart source
    // cells), drawn above the cursor to match the body's z-order
    const cellHighlights = content.cellHighlights;
    cellHighlights.position.set(translateX, translateY);
    cellHighlights.scale.set(scale, scale);
    renderer.render(cellHighlights, { clear: false });

    // pinned cell/table move preview (drawn in world coords), kept above the
    // cursor to match the body's z-order
    const cellMoving = content.cellMoving;
    cellMoving.position.set(translateX, translateY);
    cellMoving.scale.set(scale, scale);
    renderer.render(cellMoving, { clear: false });
  }

  private renderDivider(
    renderer: Renderer,
    options: {
      freezeScreenX: number;
      freezeScreenY: number;
      headingWidth: number;
      headingHeight: number;
      screenW: number;
      screenH: number;
      showVertical: boolean;
      showHorizontal: boolean;
    }
  ): void {
    const {
      freezeScreenX,
      freezeScreenY,
      headingWidth,
      headingHeight,
      screenW,
      screenH,
      showVertical,
      showHorizontal,
    } = options;
    if (!showVertical && !showHorizontal) return;

    this.divider.clear();
    this.divider.position.set(0, 0);
    this.divider.scale.set(1, 1);
    this.divider.lineStyle({
      width: 2,
      color: getCSSVariableTint('muted-foreground'),
      alpha: 0.9,
      alignment: 0.5,
      native: false,
    });

    // Each bar spans the full extent from the heading edge, so when both axes
    // are frozen the bars cross through the frozen panes (a + shape), matching
    // the freeze indicators in Excel and Google Sheets.
    if (showVertical) {
      this.divider.moveTo(freezeScreenX, headingHeight);
      this.divider.lineTo(freezeScreenX, screenH);
    }
    if (showHorizontal) {
      this.divider.moveTo(headingWidth, freezeScreenY);
      this.divider.lineTo(screenW, freezeScreenY);
    }
    renderer.render(this.divider, { clear: false });
  }

  // Draws the live freeze-drag preview line at the prospective freeze boundary,
  // in screen coords on top of the panes so it stays visible when the drag
  // moves into the frozen zone.
  private renderPreview(
    renderer: Renderer,
    headingWidth: number,
    headingHeight: number,
    screenW: number,
    screenH: number
  ): void {
    const previewState = content.headings.freezePreview;
    this.preview.clear();
    if (!previewState) return;

    this.preview.position.set(0, 0);
    this.preview.scale.set(1, 1);
    this.preview.lineStyle({ width: 2, color: content.accentColor, alpha: 1, alignment: 0.5, native: false });

    if (previewState.column !== undefined && previewState.screenX !== undefined) {
      this.preview.moveTo(previewState.screenX, headingHeight);
      this.preview.lineTo(previewState.screenX, screenH);
    }
    if (previewState.row !== undefined && previewState.screenY !== undefined) {
      this.preview.moveTo(headingWidth, previewState.screenY);
      this.preview.lineTo(screenW, previewState.screenY);
    }
    renderer.render(this.preview, { clear: false });
  }
}

export const frozenPanes = new FrozenPanes();
